<script setup>
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'emailing_automation.emailingAutomation',
            header: () => 'Campagne',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.emailingAutomation.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'step',
            header: () => "Type d'email",
            enableSorting: false,
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.step.label,
                    type: props.row.original.step.isFollowUp
                        ? 'secondary'
                        : 'primary',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'recipientEmail',
            header: () => 'Email',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.recipientEmail,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'telephone',
            header: () => 'Téléphone',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.telephone,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_carts.cms_updated_at_timestamp',
            header: () => 'Panier MAJ le',
            cell: (props) =>
                props.row.original.cartUpdatedAt
                    ? h(TextCell, {
                          text: props.row.original.cartUpdatedAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'status',
            header: () => 'Statut',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.status.label,
                    type:
                        props.row.original.status.value === 'sent'
                            ? 'success'
                            : 'primary',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'scheduledAt',
            header: () => 'Planifié le',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.scheduledAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sentAt',
            header: () => 'Envoyé le',
            cell: (props) =>
                props.row.original.sentAt
                    ? h(TextCell, {
                          text: props.row.original.sentAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'isOpened',
            header: () => 'Ouverts',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isOpened,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isClicked',
            header: () => 'Clics',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isClicked,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'hasOrder',
            header: () => 'Commandé',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.hasOrder,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_orders.grand_total',
            header: () => 'Revenu',
            cell: (props) =>
                props.row.original.orderGrandTotal
                    ? h(PriceCell, {
                          price: props.row.original.orderGrandTotal,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_orders.cms_created_at_timestamp',
            header: () => 'Date commande',
            cell: (props) =>
                props.row.original.orderCreatedAt
                    ? h(TextCell, {
                          text: props.row.original.orderCreatedAt,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'isUnsubscribed',
            header: () => 'Désabonné',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isUnsubscribed,
                }),
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingAutomationAutomatedEmails',
        {
            tenant: tenant.id,
        },
    ),
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        wrap-headers
    >
        <template #actions>
            <DateRangePicker :callback="updateData" can-select-today />
        </template>
    </DataTableLayout>
</template>
